<template>
  <v-dialog
    v-model="visible"
    max-width="700"
    scrollable
  >
    <v-card
      :title="$bct(form?.name) || undefined"
      tile
    >
      <template #append>
        <v-col>
          <v-btn
            :href="downloadLink"
            class="ms-1"
            color="primary"
            size="small"
            variant="flat"
          >
            {{ $bct('Download') }}
          </v-btn>
        </v-col>
        <v-col>
          <v-btn
            @click="close"
            icon="close"
            variant="text"
          />
        </v-col>
      </template>

      <v-card-text class="mnh-250">
        <template v-if="loaded">
          <QuestionSet
            v-model="form"
            @change:attachments="loadAttachments"
            :attachment-group-id="form.group_id"
            :attachment-owner-id="form.id"
            :attachment-owner-type="'Form'"
            :attachments="attachments"
            :questions="formQuestions"
            :schema="schema.definition"
            key-name="question"
            divided
            expanded
            hide-actions
            paddingless
            readonly
            very-dense
          />
        </template>

        <template v-else>
          <v-progress-linear
            class="my-8"
            indeterminate
          />
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useQuestionable from '@/shared/composables/useQuestionable';
import useApi from '@/shared/composables/useApi';
import { useStore } from 'vuex';

defineExpose({ open });

const props = defineProps({
  showUnpublishedQuestions: {
    type: Boolean,
    default: false,
  },
});

const { api } = useApi();
const store = useStore();
const $bct = inject('translator');

let attachments = ref([]);
let form = ref(null);
let schema = ref(null);
let visible = ref(false);

const schemaId = computed(() => schema.value?.id);

const { questions, questionsLoaded, validQuestions, validPublishedQuestions } = useQuestionable({
  ownerDataTypeRef: ref('Schema'),
  ownerIdRef: schemaId,
  syncedObjectRef: form,
});

let loaded = computed(() => questionsLoaded.value && questions.value.length > 0);
let formQuestions = computed(() =>
  props.showUnpublishedQuestions ? validQuestions.value : validPublishedQuestions.value,
);

function close() {
  visible.value = false;
}

async function loadAttachments() {
  let endpoint;
  let args = {
    owner_id: form.value.id,
    owner_type: 'Form',
  };
  if (store.state.role === 'parent') {
    endpoint = api.member.attachment;
  } else if (store.state.role === 'manager') {
    endpoint = api.manager.attachment;
    args = {
      form_id: form.value.id,
      schema_id: schema.value.id,
    };
  } else {
    endpoint = api.organization.attachment;
  }
  const { data } = await endpoint.index(args);
  attachments.value = data;
}

async function open(newForm) {
  form.value = newForm;
  schema.value = store.state.schemas[form.value.schema_id];
  visible.value = true;
  await loadAttachments();
}

const downloadLink = computed(() => {
  if (!form.value) return null;
  const role = store.state.role;

  let url;
  switch (role) {
    case 'parent':
      url = api.parent.form.downloadUrl(
        form.value.id,
        store.state.profile.default_locale,
        form.value.name,
      );
      break;
    case 'manager':
      url = api.manager.provider.form.downloadUrl(
        form.value.providerId,
        form.value.id,
        store.state.profile.default_locale,
        form.value.name,
      );
      break;
    case 'specialist':
      url = api.organization.form.downloadUrl(form.value.id, form.value.name);
      break;
  }
  return url;
});
</script>
